import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { CalendarBackendService } from 'src/app/services/calendar-backend.service';
import { OutlookCalendarService, CalendarEvent } from 'src/app/services/outlook-calendar.service';
import { CalendarOptions } from '@fullcalendar/core';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-calendar-appointment-dialog',
  templateUrl: './calendar-appointment-dialog.component.html',
  styleUrls: ['./calendar-appointment-dialog.component.scss']
})
export class CalendarAppointmentDialogComponent implements OnInit {
  /* ========================================================== ATRIBUTES ========================================================== */
  isCalendarReady = false;
  isLoading = false;
  errorMessage = '';
  successMessage = '';

  // Formulario inline para nueva cita (aparece al hacer clic en el calendario)
  showNewAppointmentForm = false;
  newAppointmentTitle = '';
  newAppointmentDescription = '';
  newAppointmentLocation = '';
  selectedStartDate = '';
  selectedEndDate = '';
  selectedDateDisplay = '';

  // Opciones de FullCalendar
  calendarOptions!: CalendarOptions;


  /* ========================================================== CONSTRUCTOR ========================================================== */
  constructor(
    public dialogRef: MatDialogRef<CalendarAppointmentDialogComponent>,
    private calendarBackend: CalendarBackendService,
    private outlookCalendar: OutlookCalendarService
  ) {
    this.initCalendarOptions();
  }


  /* ========================================================== METHODS ========================================================== */
  /* ---------------------------------------------------- Meth. Lifecycle Hooks ---------------------------------------------------- */
  ngOnInit(): void {
    // Login vía backend + cargar calendario
    this.initCalendar();
  }



  /* ---------------------------------------------------- Meth. HTML ---------------------------------------------------- */



  /* ---------------------------------------------------- Meth. Listening ---------------------------------------------------- */



  /* ---------------------------------------------------- Meth. Common ---------------------------------------------------- */

  /* ---------- Inicializar calendario: login backend + cargar eventos ---------- */
  private initCalendar(): void {
    // Mostrar sweet alert de carga
    Swal.fire({
      title: 'Obteniendo calendario...',
      text: 'Conectando con Outlook para cargar las citas',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    // Login programático vía backend (Client Credentials)
    this.calendarBackend.loginMicrosoft().subscribe({
      next: async (loginResult) => {
        if (!loginResult.success) {
          Swal.close();
          this.errorMessage = loginResult.message || 'No se pudo conectar con el calendario de Outlook.';
          return;
        }

        try {
          // Cargar eventos del calendario del doctor
          const events = await this.outlookCalendar.getEvents();

          // Mapear eventos de Graph API al formato de FullCalendar
          const calendarEvents = events.map((event: any) => ({
            title: event.subject,
            start: event.start?.dateTime,
            end: event.end?.dateTime,
            backgroundColor: '#0078d4',
            borderColor: '#0078d4',
          }));

          // Actualizar eventos del calendario
          this.calendarOptions = {
            ...this.calendarOptions,
            events: calendarEvents,
          };

          this.isCalendarReady = true;
          Swal.close();

        } catch (error) {
          console.error('Error al cargar eventos:', error);
          Swal.close();
          this.errorMessage = 'Error al obtener el calendario. Intente de nuevo más tarde.';
        }
      },
      error: (error) => {
        console.error('Error en login Microsoft:', error);
        Swal.close();
        this.errorMessage = 'Error al conectar con el servidor de calendario.';
      },
    });
  }

  /* ---------- Inicializar opciones de FullCalendar ---------- */
  private initCalendarOptions(): void {
    this.calendarOptions = {
      plugins: [timeGridPlugin, dayGridPlugin, interactionPlugin],
      initialView: 'timeGridWeek',
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay',
      },
      locale: 'es',
      firstDay: 1,
      slotMinTime: '07:00:00',
      slotMaxTime: '20:00:00',
      allDaySlot: true,
      selectable: true,
      selectMirror: true,
      dateClick: this.onDateClick.bind(this),
      select: this.onDateSelect.bind(this),
      events: [],
      height: 'auto',
      nowIndicator: true,
      eventDisplay: 'block',
    };
  }

  /* ---------- Click en un día/hora del calendario ---------- */
  onDateClick(arg: any): void {
    this.selectedStartDate = arg.dateStr;
    const endDate = new Date(arg.date);
    endDate.setHours(endDate.getHours() + 1);
    this.selectedEndDate = endDate.toISOString().slice(0, 19);
    this.selectedDateDisplay = this.formatDateDisplay(arg.dateStr);
    this.newAppointmentTitle = '';
    this.newAppointmentDescription = '';
    this.newAppointmentLocation = '';
    this.showNewAppointmentForm = true;
  }

  /* ---------- Selección de rango en el calendario ---------- */
  onDateSelect(arg: any): void {
    this.selectedStartDate = arg.startStr.slice(0, 19);
    this.selectedEndDate = arg.endStr.slice(0, 19);
    this.selectedDateDisplay = this.formatDateDisplay(arg.startStr);
    this.newAppointmentTitle = '';
    this.newAppointmentDescription = '';
    this.newAppointmentLocation = '';
    this.showNewAppointmentForm = true;
  }

  /* ---------- Formatear fecha para mostrar ---------- */
  private formatDateDisplay(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /* ---------- Crear cita desde el formulario inline ---------- */
  async createAppointment(): Promise<void> {
    if (!this.newAppointmentTitle) {
      this.errorMessage = 'El título es obligatorio.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.successMessage = '';

    const event: CalendarEvent = {
      title: this.newAppointmentTitle,
      description: this.newAppointmentDescription,
      start: this.selectedStartDate,
      end: this.selectedEndDate,
      location: this.newAppointmentLocation,
    };

    try {
      await this.outlookCalendar.createEvent(event);
      this.successMessage = '✅ Cita creada exitosamente en el calendario de Outlook.';
      this.isLoading = false;
      this.showNewAppointmentForm = false;
      this.loadCalendarEvents();
    } catch (error) {
      console.error('Error al crear cita:', error);
      this.errorMessage = 'Error al crear la cita. Intente de nuevo.';
      this.isLoading = false;
    }
  }

  /* ---------- Recargar eventos del calendario ---------- */
  private async loadCalendarEvents(): Promise<void> {
    try {
      const events = await this.outlookCalendar.getEvents();
      const calendarEvents = events.map((event: any) => ({
        title: event.subject,
        start: event.start?.dateTime,
        end: event.end?.dateTime,
        backgroundColor: '#0078d4',
        borderColor: '#0078d4',
      }));
      this.calendarOptions = { ...this.calendarOptions, events: calendarEvents };
    } catch (error) {
      console.error('Error al recargar eventos:', error);
    }
  }

  /* ---------- Cancelar formulario inline ---------- */
  cancelAppointment(): void {
    this.showNewAppointmentForm = false;
    this.errorMessage = '';
  }

  /* ---------- Cerrar dialog ---------- */
  closeDialog(): void {
    this.dialogRef.close();
  }


  /* ---------------------------------------------------- Meth. Modal load file ---------------------------------------------------- */


  /* ---------------------------------------------------- Meth. Services ---------------------------------------------------- */
}
